import { StyleSheet } from "react-native";

import { fonts, radii, spacing, type ThemeColors } from "~/styles/theme";

export const sidebarInset = spacing.m;

export const makeStylesheet = (colors: ThemeColors) =>
  StyleSheet.create({
    appShell: {
      flex: 1,
      flexDirection: "row",
      backgroundColor: colors.background,
    },
    appShellContent: { flex: 1, minWidth: 0 },
    upgradeBanner: {
      flexDirection: "row",
      flexWrap: "wrap",
      alignItems: "center",
      justifyContent: "space-between",
      gap: spacing.s,
      paddingHorizontal: spacing.m,
      paddingVertical: spacing.s,
      backgroundColor: colors.accent,
    },
    sidebar: {
      width: 248,
      padding: 20,
      backgroundColor: colors.surface,
      borderRightWidth: 1,
      borderRightColor: colors.borderStrong,
    },
    sidebarBody: { flex: 1, gap: spacing.s },
    sidebarBrand: {
      marginBottom: 20,
    },
    syncPill: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.s,
      marginBottom: spacing.m,
      paddingHorizontal: spacing.m,
      paddingVertical: spacing.s,
      borderWidth: 1,
      borderColor: colors.borderStrong,
      borderRadius: radii.m,
      backgroundColor: colors.elevated,
    },
    syncDot: {
      width: 7,
      height: 7,
      borderRadius: 4,
      backgroundColor: colors.primary,
    },
    syncDotPending: { backgroundColor: colors.accent },
    syncDotError: { backgroundColor: colors.danger },
    syncLabel: {
      flex: 1,
      minWidth: 0,
      color: colors.heading,
      fontFamily: fonts.medium,
      fontSize: 12,
    },
    sidebarDivider: {
      paddingTop: spacing.l,
      marginTop: spacing.l,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: colors.border,
    },
    navItem: {
      minHeight: 44,
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.m,
      paddingHorizontal: sidebarInset,
      borderRadius: radii.m,
      borderLeftWidth: 3,
      borderLeftColor: "transparent",
    },
    navItemActive: {
      backgroundColor: colors.elevated,
      borderLeftColor: colors.accent,
    },
    navItemHover: { backgroundColor: "rgba(28, 35, 33, 0.05)" },
    navItemText: {
      color: colors.mutedForeground,
      fontFamily: fonts.medium,
      fontSize: 14,
    },
    navItemTextActive: { color: colors.heading, fontFamily: fonts.semibold },
    sidebarUtilityLink: {
      minHeight: 36,
      justifyContent: "center",
      paddingHorizontal: sidebarInset,
      paddingVertical: 6,
      borderRadius: radii.m,
    },
    sidebarUtilityText: {
      color: colors.heading,
      fontFamily: fonts.regular,
      fontSize: 14,
    },
    sidebarUtilityTextActive: {
      color: colors.primary,
      fontFamily: fonts.medium,
    },
    utilityContent: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: spacing.s,
    },
    planBadge: {
      minWidth: 18,
      height: 18,
      alignItems: "center",
      justifyContent: "center",
      paddingHorizontal: 6,
      borderRadius: radii.s,
      backgroundColor: colors.accent,
    },
    planBadgeText: {
      color: colors.heading,
      fontFamily: fonts.bold,
      fontSize: 10,
    },
    sidebarFooter: {
      gap: spacing.s,
      paddingTop: spacing.l,
      borderTopWidth: 1,
      borderTopColor: colors.border,
    },
    sidebarProfile: {
      minHeight: 44,
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      borderRadius: radii.m,
    },
    sidebarAvatar: {
      width: 34,
      height: 34,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: radii.m,
      backgroundColor: colors.primary,
    },
    sidebarAvatarText: {
      color: colors.primaryForeground,
      fontFamily: fonts.bold,
      fontSize: 12,
    },
    sidebarProfileName: {
      flex: 1,
      color: colors.heading,
      fontFamily: fonts.medium,
      fontSize: 13,
    },
    shellHeader: {
      minHeight: 56,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: spacing.s,
      paddingHorizontal: spacing.l,
      borderBottomWidth: 1,
      borderBottomColor: colors.borderStrong,
      backgroundColor: colors.elevated,
    },
    shellHeaderLeft: {
      flex: 1,
      minWidth: 0,
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.s,
    },
    shellActions: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.s,
    },
    headerIconButton: {
      width: 40,
      height: 40,
      alignItems: "center",
      justifyContent: "center",
      borderWidth: 1,
      borderColor: colors.borderStrong,
      borderRadius: radii.m,
      backgroundColor: colors.elevated,
    },
    headerIconButtonHover: { backgroundColor: colors.surface },
    breadcrumbs: {
      flex: 1,
      minWidth: 0,
      flexDirection: "row",
      alignItems: "center",
    },
    breadcrumbItem: {
      flexShrink: 1,
      minWidth: 0,
      flexDirection: "row",
      alignItems: "center",
    },
    breadcrumbCrumb: {
      flexShrink: 1,
      color: colors.mutedForeground,
      fontFamily: fonts.medium,
      fontSize: 13,
    },
    breadcrumbCurrent: {
      color: colors.heading,
      fontFamily: fonts.semibold,
    },
    breadcrumbSeparator: {
      color: colors.mutedForeground,
      fontFamily: fonts.regular,
      fontSize: 12,
      marginHorizontal: spacing.xs,
    },
    drawerOverlay: {
      flex: 1,
      flexDirection: "row",
      backgroundColor: colors.overlay,
    },
    drawerPanel: {
      width: "82%",
      maxWidth: 320,
      padding: 20,
      backgroundColor: colors.surface,
    },
    drawerDismiss: { flex: 1 },
    modalOverlay: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      padding: spacing.l,
      backgroundColor: colors.overlay,
    },
    confirmationCard: {
      width: "100%",
      maxWidth: 420,
      padding: spacing.xl,
      borderRadius: radii.card,
      borderWidth: 1,
      borderColor: colors.borderStrong,
      backgroundColor: colors.elevated,
      gap: spacing.l,
    },
    modalTitle: {
      color: colors.heading,
      fontFamily: fonts.bold,
      fontSize: 21,
    },
    confirmationActions: {
      flexDirection: "row",
      justifyContent: "flex-end",
      gap: spacing.s,
    },
  });
